import dayjs from "dayjs";
import durationPlugin from "dayjs/plugin/duration";
import isTodayPlugin from "dayjs/plugin/isToday";
import relativeTimePlugin from "dayjs/plugin/relativeTime";
import utcPlugin from "dayjs/plugin/utc";

import type { TFunction } from "i18next";

dayjs.extend(durationPlugin);
dayjs.extend(isTodayPlugin);
dayjs.extend(relativeTimePlugin);
dayjs.extend(utcPlugin);

export const DATE_FORMAT = "D MMM YYYY";
export const DATE_SHORT_FORMAT = "DD/MM/YYYY";
export const DATE_FORMAT_LONG = "D MMMM YYYY";
export const DATE_TIME_FORMAT = "DD/MM/YYYY HH:mm";
export const DATE_SHORT_WITH_TIME_FORMAT = "MMM D, hh.mm A";

export function getMonths(t: TFunction<"translation", undefined>) {
  return [
    t("months.january"),
    t("months.february"),
    t("months.march"),
    t("months.april"),
    t("months.may"),
    t("months.june"),
    t("months.july"),
    t("months.august"),
    t("months.september"),
    t("months.october"),
    t("months.november"),
    t("months.december"),
  ];
}

export function formatBreakdownDate(value?: string) {
  return dayjs(value).format("YYYY-MM");
}

export function formatDate(value?: string) {
  return dayjs(value).format(DATE_FORMAT);
}

export function formatShortDate(value?: string) {
  if (!value) return "";
  return dayjs(value).format(DATE_SHORT_FORMAT);
}

export function formatDateLong(value?: string) {
  if (!value) return "";
  return dayjs(value).format(DATE_FORMAT_LONG);
}

export function formatDateTime(value?: string) {
  return dayjs(value).format(DATE_TIME_FORMAT);
}

export function formatRelativeTime(value?: string, from?: string) {
  if (from) {
    return dayjs(value).from(dayjs(from));
  }
  return dayjs(value).fromNow();
}

export function isToday(value?: string) {
  return dayjs(value).isToday();
}

function formatSingleUtcDate(date: dayjs.Dayjs) {
  return `${date.format("MMM")} ${date.date()}, ${date.format("YYYY")}`;
}

export function formatDateRange(start?: string, end?: string) {
  if (!start && !end) return "";

  if (!start && end) {
    return formatSingleUtcDate(dayjs.utc(end));
  }

  if (start && !end) {
    return formatSingleUtcDate(dayjs.utc(start));
  }

  const startDate = dayjs.utc(start);
  const endDate = dayjs.utc(end);
  const startDay = startDate.date();
  const endDay = endDate.date();
  const startMonth = startDate.format("MMM");
  const endMonth = endDate.format("MMM");
  const startYear = startDate.format("YYYY");
  const endYear = endDate.format("YYYY");

  if (startDay === endDay && startMonth === endMonth && startYear === endYear) {
    // Single-day event: Jul 20, 2025
    return formatSingleUtcDate(startDate);
  } else if (startMonth === endMonth && startYear === endYear) {
    // Same month and year: Jul 20-21, 2025
    return `${startMonth} ${startDay}-${endDay}, ${startYear}`;
  } else if (startYear === endYear) {
    // Different months but same year: Jul 30-Aug 2, 2025
    return `${startMonth} ${startDay}-${endMonth} ${endDay}, ${startYear}`;
  } else {
    // Different years: Dec 30, 2024-Jan 2, 2025
    return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
  }
}

export function formatShortDateWithTime(value?: string) {
  if (!value) return "";
  return dayjs(value).format(DATE_SHORT_WITH_TIME_FORMAT);
}
